import React from 'react';
import dayjs from 'dayjs';
import { PrintPageHeader, PrintFooter, printStyles } from './PrintBase';

const formatCurrency = (val) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val || 0);

function calcularResumen(movs) {
  if (!movs || movs.length === 0) {
    return {
      totalRegistros: 0,
      entradas_count: 0,
      entradas_qty: 0,
      entradas_valor: 0,
      salidas_count: 0,
      salidas_qty: 0,
      salidas_valor: 0,
    };
  }
  const entradas = movs.filter((m) => m.tipo === 'entrada');
  const salidas = movs.filter((m) => m.tipo === 'salida');

  const sumaQty = (arr) => arr.reduce((acc, m) => acc + parseFloat(m.cantidad || 0), 0);
  const sumaValor = (arr) =>
    arr.reduce((acc, m) => acc + parseFloat(m.cantidad || 0) * parseFloat(m.precio_unitario || 0), 0);

  return {
    totalRegistros: movs.length,
    entradas_count: entradas.length,
    entradas_qty: sumaQty(entradas),
    entradas_valor: sumaValor(entradas),
    salidas_count: salidas.length,
    salidas_qty: sumaQty(salidas),
    salidas_valor: sumaValor(salidas),
  };
}

function buildFilterChips(filters) {
  if (!filters) return [{ label: 'Período', value: 'Todos los registros' }];
  const chips = [];
  if (filters.tipo) chips.push({ label: 'Tipo', value: filters.tipo === 'entrada' ? 'Entrada' : 'Salida' });
  if (filters.search) chips.push({ label: 'Búsqueda', value: filters.search });
  if (filters.fechas && filters.fechas[0]) {
    chips.push({
      label: 'Período',
      value: `${filters.fechas[0].format('DD/MM/YYYY')} – ${filters.fechas[1].format('DD/MM/YYYY')}`,
    });
  }
  if (chips.length === 0) chips.push({ label: 'Período', value: 'Todos los registros' });
  return chips;
}

const MovimientosPrint = React.forwardRef(function MovimientosPrint({ data, filters }, ref) {
  const movs = data || [];
  const resumen = calcularResumen(movs);
  const infoRows = buildFilterChips(filters);

  // Totals for summary row
  const totalCantidadEntradas = movs
    .filter((m) => m.tipo === 'entrada')
    .reduce((acc, m) => acc + parseFloat(m.cantidad || 0), 0);
  const totalCantidadSalidas = movs
    .filter((m) => m.tipo === 'salida')
    .reduce((acc, m) => acc + parseFloat(m.cantidad || 0), 0);
  const totalValor = resumen.entradas_valor + resumen.salidas_valor;

  return (
    <div ref={ref} style={printStyles.page}>
      <PrintPageHeader
        titulo="Reporte de Movimientos de Inventario"
        subtitulo={`${resumen.totalRegistros} registro(s) encontrado(s)`}
        infoRows={infoRows}
      />

      {/* Summary Grid */}
      <div style={printStyles.summaryGrid}>
        <div style={printStyles.summaryCard}>
          <p style={printStyles.summaryLabel}>Total Registros</p>
          <p style={{ ...printStyles.summaryValue, color: '#1677ff' }}>
            {resumen.totalRegistros}
          </p>
        </div>
        <div style={printStyles.summaryCard}>
          <p style={printStyles.summaryLabel}>
            Entradas ({resumen.entradas_count} mov.)
          </p>
          <p style={{ ...printStyles.summaryValue, color: '#389e0d' }}>
            {resumen.entradas_qty.toLocaleString('es-MX')} uds.
          </p>
        </div>
        <div style={printStyles.summaryCard}>
          <p style={printStyles.summaryLabel}>
            Salidas ({resumen.salidas_count} mov.)
          </p>
          <p style={{ ...printStyles.summaryValue, color: '#cf1322' }}>
            {resumen.salidas_qty.toLocaleString('es-MX')} uds.
          </p>
        </div>
        <div style={printStyles.summaryCard}>
          <p style={printStyles.summaryLabel}>Valor Total Movido</p>
          <p style={{ ...printStyles.summaryValue, fontSize: '10pt' }}>
            {formatCurrency(totalValor)}
          </p>
        </div>
      </div>

      {/* Value detail lines */}
      <div style={{ display: 'flex', gap: '12pt', marginBottom: '10pt', fontSize: '8pt', color: '#555' }}>
        <span>
          Valor entradas:{' '}
          <strong style={{ color: '#389e0d' }}>{formatCurrency(resumen.entradas_valor)}</strong>
        </span>
        <span>
          Valor salidas:{' '}
          <strong style={{ color: '#cf1322' }}>{formatCurrency(resumen.salidas_valor)}</strong>
        </span>
      </div>

      {/* Movements detail table */}
      <div style={printStyles.sectionTitle}>Detalle de Movimientos</div>
      <table style={printStyles.table}>
        <thead>
          <tr>
            <th style={printStyles.th}>Fecha</th>
            <th style={printStyles.th}>Clave</th>
            <th style={printStyles.th}>Producto</th>
            <th style={{ ...printStyles.th, textAlign: 'center' }}>Tipo</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Cantidad</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Stk. Anterior</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Stk. Posterior</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Precio Unit.</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Valor Total</th>
            <th style={printStyles.th}>Motivo</th>
            <th style={printStyles.th}>Referencia</th>
            <th style={printStyles.th}>Personal</th>
            <th style={printStyles.th}>Notas</th>
          </tr>
        </thead>
        <tbody>
          {movs.map((mov) => {
            const esEntrada = mov.tipo === 'entrada';
            const valorTotal = parseFloat(mov.cantidad || 0) * parseFloat(mov.precio_unitario || 0);
            return (
              <tr key={mov.id} style={esEntrada ? printStyles.trEntrada : printStyles.trSalida}>
                <td style={printStyles.td}>
                  {dayjs(mov.created_at).format('DD/MM/YYYY HH:mm')}
                </td>
                <td style={printStyles.td}>{mov.producto_clave || '—'}</td>
                <td style={printStyles.td}>{mov.producto_nombre || '—'}</td>
                <td style={{ ...printStyles.tdCenter, fontWeight: 'bold', color: esEntrada ? '#389e0d' : '#cf1322' }}>
                  {esEntrada ? '↑ Entrada' : '↓ Salida'}
                </td>
                <td style={printStyles.tdRight}>
                  {parseFloat(mov.cantidad || 0).toLocaleString('es-MX')} {mov.unidad_medida || ''}
                </td>
                <td style={printStyles.tdRight}>
                  {parseFloat(mov.stock_anterior || 0).toLocaleString('es-MX')}
                </td>
                <td style={{ ...printStyles.tdRight, fontWeight: 'bold', color: esEntrada ? '#389e0d' : '#cf1322' }}>
                  {parseFloat(mov.stock_posterior || 0).toLocaleString('es-MX')}
                </td>
                <td style={printStyles.tdRight}>
                  {parseFloat(mov.precio_unitario || 0) > 0
                    ? formatCurrency(mov.precio_unitario)
                    : '—'}
                </td>
                <td style={printStyles.tdRight}>
                  {valorTotal > 0 ? formatCurrency(valorTotal) : '—'}
                </td>
                <td style={printStyles.td}>{mov.motivo || '—'}</td>
                <td style={printStyles.td}>{mov.referencia || '—'}</td>
                <td style={printStyles.td}>{mov.personal || '—'}</td>
                <td style={printStyles.td}>{mov.notas || '—'}</td>
              </tr>
            );
          })}
          {movs.length === 0 && (
            <tr>
              <td
                colSpan={13}
                style={{ ...printStyles.tdCenter, color: '#888', fontStyle: 'italic' }}
              >
                Sin movimientos registrados
              </td>
            </tr>
          )}
        </tbody>
        {movs.length > 0 && (
          <tfoot>
            <tr style={printStyles.trSummary}>
              <td colSpan={4} style={{ ...printStyles.td, fontWeight: 'bold' }}>
                TOTALES ({resumen.totalRegistros} registros)
              </td>
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold' }}>
                <div style={{ color: '#389e0d' }}>
                  ↑ {totalCantidadEntradas.toLocaleString('es-MX')}
                </div>
                <div style={{ color: '#cf1322' }}>
                  ↓ {totalCantidadSalidas.toLocaleString('es-MX')}
                </div>
              </td>
              <td style={printStyles.tdRight} />
              <td style={printStyles.tdRight} />
              <td style={printStyles.tdRight} />
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold' }}>
                {formatCurrency(totalValor)}
              </td>
              <td colSpan={4} style={printStyles.td} />
            </tr>
          </tfoot>
        )}
      </table>

      <PrintFooter />
    </div>
  );
});

export default MovimientosPrint;
